import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronRight, EyeOff } from 'lucide-react'
import Section, { KeyValues, Muted } from './Section'
import StatusPill, { Tag } from './StatusPill'
import JsonViewer, { JsonBlock } from './JsonViewer'
import {
  fmtClockMs, fmtDay, fmtFull, fmtValue, outcomeText, postbackReasonLabel, sourceLabel, stepTone,
} from './model'

// Every field as submitted, already masked by the backend. Nested values
// stay nested, shown as JSON.
export function SubmittedFields({ fields }) {
  const entries = Object.entries(fields || {})
  return (
    <Section id="li-fields" title="All submitted fields" subtitle="As submitted, with consumer data masked">
      {entries.length === 0 ? <Muted>No fields were submitted.</Muted> : (
        <KeyValues label="Submitted fields" rows={entries.map(([k, v]) => [k, <span key={k} className={typeof v === 'object' && v !== null ? 'li-mono' : undefined}>{fmtValue(v)}</span>])} />
      )}
    </Section>
  )
}

// Only what was recorded: a row with nothing in it is left out, and a lead
// with no tracking at all says so once instead of ten dashes.
export function TrackingConsent({ tracking, timezone }) {
  const t = tracking || {}
  const mono = (v) => (v ? <span className="li-mono">{v}</span> : null)
  const rows = trackingRows(t, timezone, mono).filter(([, value]) => value !== null && value !== undefined && value !== '')
  return (
    <Section id="li-tracking" title="Tracking & consent">
      {rows.length === 0
        ? <Muted>No tracking data recorded.</Muted>
        : <KeyValues label="Tracking and consent" rows={rows} />}
    </Section>
  )
}

function trackingRows(t, timezone, mono) {
  return [
    ['Sub ID 1', mono(t.sub_ids?.sub1)],
    ['Sub ID 2', mono(t.sub_ids?.sub2)],
    ['Sub ID 3', mono(t.sub_ids?.sub3)],
    ['Click ID', mono(t.click_id)],
    ['IP address', mono(t.ip)],
    ['User agent', t.user_agent],
    ['TrustedForm certificate', t.trustedform_cert_url && (
      <a href={t.trustedform_cert_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', overflowWrap: 'anywhere' }}>{t.trustedform_cert_url}</a>
    )],
    ['Jornaya LeadiD', mono(t.jornaya_leadid)],
    ['Consent method', t.consent?.method],
    ['Consent timestamp', t.consent?.timestamp && (Number.isNaN(new Date(t.consent.timestamp).getTime()) ? t.consent.timestamp : `${fmtFull(t.consent.timestamp, timezone)} ET`)],
  ]
}

export function Outbound({ outbound, timezone }) {
  const { redirect = {}, emails = [], postback = {} } = outbound || {}
  const when = (at) => (at ? `${fmtFull(at, timezone)} ET` : null)
  return (
    <Section id="li-outbound" title="Redirects, emails & postbacks">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Sub title="Redirect">
          <KeyValues
            label="Redirect"
            rows={[
              ['URL', redirect.url ? <span className="li-mono">{redirect.url}</span> : (redirect.attempted_at ? <span style={{ color: 'var(--text-muted)' }}>Not shown</span> : null)],
              ['Attempted', when(redirect.attempted_at) || <span style={{ color: 'var(--text-muted)' }}>No redirect</span>],
            ]}
          />
        </Sub>
        <Sub title="Emails">
          {emails.length === 0 ? <Muted>No emails for this lead.</Muted> : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {emails.map((e, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap', fontSize: 13 }}>
                  <Tag tone={stepTone(e.status)}>{outcomeText(e.status)}</Tag>
                  <span style={{ fontWeight: 600 }}>{e.template || 'Email'}</span>
                  {e.at && <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{when(e.at)}</span>}
                  {e.error && <span style={{ fontSize: 12, color: 'var(--red)', overflowWrap: 'anywhere', flexBasis: '100%' }}>{typeof e.error === 'string' ? e.error : JSON.stringify(e.error)}</span>}
                </li>
              ))}
            </ul>
          )}
        </Sub>
        <Sub title="Partner postback">
          <KeyValues
            label="Partner postback"
            rows={[
              ['Result', <Tag key="r" tone={stepTone(postback.sent ? 'sent' : postback.at ? 'failed' : null)}>{postback.sent ? 'Sent' : postback.at ? 'Failed' : 'Not sent'}</Tag>],
              ['When', when(postback.at)],
              ['HTTP status', postback.status_code ? String(postback.status_code) : null],
              ['Why', postbackReasonLabel(postback.reason)],
            ]}
          />
        </Sub>
      </div>
    </Section>
  )
}

function Sub({ title, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <h3 style={{ margin: 0, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-muted)' }}>{title}</h3>
      {children}
    </div>
  )
}

// Every Event recorded for the lead, with its payload behind a toggle. An
// event that mentions a hidden buyer has no payload and says so.
export function EventLog({ events, timezone }) {
  const [open, setOpen] = useState(() => new Set())
  const toggle = (id) => setOpen((s) => {
    const next = new Set(s)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    return next
  })
  return (
    <Section id="li-events" title="Event log" subtitle={`${events.length} recorded`}>
      {events.length === 0 ? <Muted>No events recorded.</Muted> : (
        <div className="li-table-scroll" style={{ margin: '0 -16px' }}>
          <table className="li-table">
            <thead>
              <tr>
                <th scope="col">Time (ET)</th>
                <th scope="col">Event</th>
                <th scope="col" className="li-hide-sm">Source</th>
                <th scope="col">Summary</th>
                <th scope="col"><span style={srOnly}>Payload</span></th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => {
                const isOpen = open.has(e.id)
                const hasPayload = !e.details_hidden && e.payload !== null && e.payload !== undefined
                return (
                  <React.Fragment key={e.id}>
                    <tr>
                      <td className="li-mono" style={{ whiteSpace: 'nowrap' }}>
                        {fmtClockMs(e.at, timezone)}
                        <div style={{ fontSize: 10, color: 'var(--text-muted)' }}>{fmtDay(e.at, timezone)}</div>
                      </td>
                      <td style={{ fontWeight: 600 }}>{e.label}</td>
                      <td className="li-hide-sm" style={{ color: 'var(--text-secondary)' }}>{sourceLabel(e.source)}</td>
                      <td style={{ color: 'var(--text-secondary)', overflowWrap: 'anywhere', minWidth: 160 }}>
                        {e.details_hidden
                          ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--text-muted)' }}><EyeOff size={12} aria-hidden="true" /> Details hidden</span>
                          : e.summary || <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                      <td style={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                        {hasPayload && (
                          <button type="button" className="li-toggle" aria-expanded={isOpen} onClick={() => toggle(e.id)} style={{ fontSize: 12, color: 'var(--accent)' }}>
                            {isOpen ? <ChevronDown size={13} aria-hidden="true" /> : <ChevronRight size={13} aria-hidden="true" />} JSON
                          </button>
                        )}
                      </td>
                    </tr>
                    {isOpen && hasPayload && (
                      <tr>
                        <td colSpan={5} style={{ background: 'var(--bg-base)' }}>
                          <JsonBlock value={e.payload} label={`${e.label} payload`} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  )
}

export function RawPayload({ payload }) {
  return (
    <Section id="li-raw" title="Raw payload" subtitle="The submission as received, with consumer data masked">
      {payload ? <JsonViewer value={payload} label="raw payload" /> : <Muted>No payload stored.</Muted>}
    </Section>
  )
}

// Other leads with the same email or phone.
export function RelatedLeads({ related, timezone }) {
  return (
    <Section id="li-related" title="Related leads" subtitle="Same email or phone">
      {related.length === 0 ? <Muted>No other leads with this email or phone.</Muted> : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {related.map((r) => (
            <li key={r.lead_id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 13 }}>
              <Link to={`/lead-inspector/${r.lead_id}`} className="li-mono" style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 600 }}>
                #{r.lead_id}
              </Link>
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{fmtFull(r.received_at, timezone)} ET</span>
              <StatusPill status={r.status} />
            </li>
          ))}
        </ul>
      )}
    </Section>
  )
}

const srOnly = { position: 'absolute', width: 1, height: 1, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }
